import fetch from 'isomorphic-fetch';
import humps from 'humps';
import NetworkError from '../utils/error/NetworkError';

const API_ORIGIN = process.env.PUBLIC_URL;
const API_ENDPOINT = process.env.API_ENDPOINT;
const API_PAGINATION_MAX_LIMIT = 100;

const isBlob = typeof Blob === 'function'
  ? value => value instanceof Blob
  : () => false;

function asUrlEncoded(object) {
  return Object.keys(object)
    .filter(k => object[k] !== undefined)
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(object[k])}`)
    .join('&');
}

function flattenObject(value, into = {}, currentPath = '') {
  if (Array.isArray(value)) {
    value.forEach((item, idx) => flattenObject(
      item,
      into,
      `${currentPath}[${idx}]`
    ));
  } else if (Object(value) === value && !isBlob(value)) {
    Object.keys(value).forEach(key => flattenObject(
      value[key],
      into,
      currentPath ? `${currentPath}[${key}]` : key
    ));
  } else if (value !== undefined) {
    into[currentPath] = value;
  }

  return into;
}

export function request(path, {qs = {}, body = undefined, headers = {}, raw, ...options}) {
  return (dispatch, getState) => {
    const state = getState();
    const {activeChannel, activeLocale} = state.channels;

    let url = [API_ENDPOINT, activeChannel, activeLocale, path].join('/');

    const flatQs = flattenObject(qs);
    if (Object.keys(flatQs).length) {
      url += '?' + asUrlEncoded(flatQs);
    }

    if (Object(body) === body) {
      const flat = flattenObject(body);

      if (Object.values(flat).some(isBlob)) {
        body = new FormData();
        Object.keys(flat).forEach(key => body.append(key, flat[key]));
      } else {
        body = asUrlEncoded(flat);
        headers['Content-Type'] = 'application/x-www-form-urlencoded';
      }
    }

    headers['Accept'] = 'application/json';
    headers['Origin'] = API_ORIGIN;

    return fetch(url, {headers, body, ...options})
      .then((response) => {
        const contentType = response.headers.get('content-type');

        return Promise.all([
          response,
          //(contentType && contentType.includes('application/json')) ? response.json() : {}
          // FIXME: Correct the API responses, then remove this hack
          response.text().then(text => {
            try {
              return JSON.parse(text);
            } catch (e) {
              return {};
            }
          })
        ]);
      })
      .then(([response, json]) => {
        if (!raw) {
          json = humps.camelizeKeys(json);
        }

        if (response.ok) {
          const {embedded, links, ...rest} = json;
          return embedded ? {content: embedded, meta: {links, ...rest}} : {content: json};
        }

        throw new NetworkError(response.status, json, {
          method: options.method || 'GET',
          headers, url, body
        });
      });
  }
}

export default {
  get(path, qs) {
    return request(path, {qs});
  },

  getAll(path, qs) {
    return dispatch => dispatch(this.get(path, {...qs, page: 1, limit: API_PAGINATION_MAX_LIMIT}))
      .then(apiResponse => {
        if (apiResponse.meta.pages === 1) {
          return apiResponse;
        }

        const requestActions = (new Array(apiResponse.meta.pages - 1))
          .fill()
          .map((_, idx) => this.get(path, {...qs, page: idx + 2, limit: API_PAGINATION_MAX_LIMIT}));

        return Promise.all(requestActions.map(dispatch))
          .then(responses => ({
            ...apiResponse,
            content: {
              ...apiResponse.content,
              items: apiResponse.content.items.concat(...responses.map(it => it.content.items))
            }
          }));
      });
  },

  post(path, body, qs)
  {
    return request(path, {
      method: 'POST',
      body,
      qs
    });
  },

  put(path, body, qs) {
    return request(path, {
      method: 'PUT',
      body,
      qs
    });
  },

  delete(path, body, qs) {
    return request(path, {
      method: 'DELETE',
      body,
      qs
    });
  }
}



// WEBPACK FOOTER //
// ./ducks/api.js